import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";
import { toastService } from "../../services/toastService";

export const registerCompany = createAsyncThunk(
  "company/registerCompany",
  async ({ payload, onSuccess }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/v1/Event/Register`, payload);
      const data = response?.data;
      onSuccess?.(data);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error creating invoice"
      );
    }
  }
);

export const getExistingCompanies = createAsyncThunk(
  "company/getExistingCompanies",
  async (accountName, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/v1/Account/getBusinessCards?accountName=${accountName}`
      );
      const data = response?.data.businesses;
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error fetching not invoiced items"
      );
    }
  }
);

export const addCompany = createAsyncThunk(
  "company/addCompany",
  async ({ payload, onSuccess }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/v1/Account/addBusinessAccount`,
        payload
      );
      const data = response?.data;
      onSuccess?.(data);
      return data;
    } catch (err) {
      toastService.error(err.response?.data?.[0] || "Error Adding Company");
      return rejectWithValue(
        err.response?.data?.message || "Error Adding Company"
      );
    }
  }
);

export const getContacts = createAsyncThunk(
  "company/getContacts",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/Profile/GetProfile?id=${id}`, {
        meta: { base: "company_api" },
      });
      const contactResponse = await axiosInstance.get(
        `/accounts/${id}/contacts`,
        {
          meta: { base: "contact_api" },
        }
      );
      const data = response?.data;
      const contacts = contactResponse?.data;
      return { ...data, contacts, physicalAddress: data?.address };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error creating invoice"
      );
    }
  }
);

export const getSuggestions = createAsyncThunk(
  "company/getSuggestions",
  async ({ address, country }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/v1/Streets/GetSuggestion?address=${address}&country=${country}`
      );
      const data = response?.data;
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error getting suggestions"
      );
    }
  }
);

export const validateAddressV2 = createAsyncThunk(
  "company/validateAddressV2",
  async ({ city, country, line1, state, zip }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/v1/Streets/ValidateAddress?line1=${line1}&city=${city}&state=${state}&zip=${zip}&country=${country}&targetEntity=`
      );
      const data = response?.data;
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error validating address"
      );
    }
  }
);

export const companyBuyerHandler = createAsyncThunk(
  "company/companyBuyerHandler",
  async ({ payload, onSuccess }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `/accounts/set-company-buyer`,
        payload,
        {
          meta: { base: "contact_api" },
        }
      );
      const data = response?.data;
      onSuccess?.(data);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error setting company buyer"
      );
    }
  }
);

export const getBidderRange = createAsyncThunk(
  "company/getBidderRange",
  async (eventId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/v1/Event/GetHouseNumbers?Event_ID=${eventId}`
      );
      const data = response?.data;
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error fetching bidder range"
      );
    }
  }
);

export const editBidderNumberHandle = createAsyncThunk(
  "company/editBidderNumberHandle",
  async ({ payload, onSuccess, onError }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/v1/Event/EditBidderNumber`,
        payload
      );
      const data = response?.data;
      onSuccess?.(data);
      return data;
    } catch (err) {
      onError?.(err);
      return rejectWithValue(
        err.response?.data?.message || "Error editing bidder number"
      );
    }
  }
);

export const updateCompanyProfile = createAsyncThunk(
  "company/updateCompanyProfile",
  async ({ payload, onSuccess, onError }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `/Profile/UpdateProfile`,
        payload,
        {
          meta: { base: "company_api" },
        }
      );
      const data = response?.data;
      onSuccess?.(data);
      return data;
    } catch (err) {
      onError?.(err);
      console.log(err);
      return rejectWithValue("Failed to update company profile");
    }
  }
);

export const getCompanyProfile = createAsyncThunk(
  "company/getProfile",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/v1/Streets/ValidateAddress?line1=${line1}&city=${city}&state=${state}&zip=${zip}&country=${country}&targetEntity=`
      );
      const data = response?.data;
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error validating address"
      );
    }
  }
);

export const editTaxExempt = createAsyncThunk(
  "company/editTaxExempt",
  async ({ payload, onSuccess }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `/Profile/UpdateBilling`,
        payload,
        {
          meta: { base: "company_api" },
        }
      );
      const data = response?.data;
      onSuccess?.(data);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error editing contact"
      );
    }
  }
);

const companySlice = createSlice({
  name: "searchCompany",
  initialState: {
    data: [],
    error: null,
    loading: false,
    addCompanyError: null,
    addCompanyloading: false,
    addCompanyRes: null,
    registerCompanyError: null,
    registerCompanyloading: false,
    registerCompanyRes: null,
    getContactsError: null,
    getContactsloading: false,
    getContactsRes: {},
    getSuggestionsError: null,
    getSuggestionsloading: false,
    getSuggestionsRes: {},
    validateAddressError: null,
    validateAddressloading: false,
    validateAddressRes: {},
    companyBuyerHandlerError: null,
    companyBuyerHandlerloading: false,
    companyBuyerHandlerRes: {},
    getBidderRangeError: null,
    getBidderRangeloading: false,
    getBidderRangeRes: {},
    editBidderNumberError: null,
    editBidderNumberloading: false,
    editBidderNumberRes: {},
    updateCompanyProfileLoading: false,
    editTaxExemptLoading: false,
  },
  reducers: {
    clearAddress: (state) => {
      state.validateAddressRes = {};
      state.validateAddressError = null;
    },
    clearContactsRes: (state) => {
      state.getContactsRes = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerCompany.pending, (state) => {
        state.registerCompanyloading = true;
      })
      .addCase(registerCompany.fulfilled, (state, action) => {
        state.registerCompanyloading = false;
        state.registerCompanyRes = action.payload;
      })
      .addCase(registerCompany.rejected, (state, action) => {
        state.registerCompanyError = action.payload;
        state.registerCompanyloading = false;
      })

      .addCase(getExistingCompanies.pending, (state) => {
        state.loading = true;
      })
      .addCase(getExistingCompanies.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(getExistingCompanies.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })

      .addCase(addCompany.pending, (state) => {
        state.addCompanyloading = true;
      })
      .addCase(addCompany.fulfilled, (state, action) => {
        state.addCompanyloading = false;
        state.addCompanyRes = action.payload;
      })
      .addCase(addCompany.rejected, (state, action) => {
        state.addCompanyError = action.payload;
        state.addCompanyloading = false;
      })

      .addCase(getContacts.pending, (state) => {
        state.getContactsloading = true;
      })
      .addCase(getContacts.fulfilled, (state, action) => {
        state.getContactsloading = false;
        state.getContactsRes = action.payload;
      })
      .addCase(getContacts.rejected, (state, action) => {
        state.getContactsError = action.payload;
        state.getContactsloading = false;
      })

      .addCase(getSuggestions.pending, (state) => {
        state.getSuggestionsloading = true;
      })
      .addCase(getSuggestions.fulfilled, (state, action) => {
        state.getSuggestionsloading = false;
        state.getSuggestionsRes = action.payload;
      })
      .addCase(getSuggestions.rejected, (state, action) => {
        state.getSuggestionsError = action.payload;
        state.getSuggestionsloading = false;
      })

      .addCase(validateAddressV2.pending, (state) => {
        state.validateAddressloading = true;
      })
      .addCase(validateAddressV2.fulfilled, (state, action) => {
        state.validateAddressloading = false;
        state.validateAddressRes = action.payload;
      })
      .addCase(validateAddressV2.rejected, (state, action) => {
        state.validateAddressError = action.payload;
        state.validateAddressloading = false;
      })
      .addCase(companyBuyerHandler.pending, (state) => {
        state.companyBuyerHandlerloading = true;
      })
      .addCase(companyBuyerHandler.fulfilled, (state, action) => {
        state.companyBuyerHandlerloading = false;
        state.companyBuyerHandlerRes = action.payload;
      })
      .addCase(companyBuyerHandler.rejected, (state, action) => {
        state.companyBuyerHandlerError = action.payload;
        state.companyBuyerHandlerloading = false;
      })
      .addCase(getBidderRange.pending, (state) => {
        state.getBidderRangeloading = true;
      })
      .addCase(getBidderRange.fulfilled, (state, action) => {
        state.getBidderRangeRes = action.payload;
        state.getBidderRangeloading = false;
      })
      .addCase(getBidderRange.rejected, (state, action) => {
        state.getBidderRangeError = action.payload;
        state.getBidderRangeloading = false;
      })
      .addCase(editBidderNumberHandle.pending, (state) => {
        state.editBidderNumberloading = true;
      })
      .addCase(editBidderNumberHandle.fulfilled, (state, action) => {
        state.editBidderNumberloading = false;
        state.editBidderNumberRes = action.payload;
      })
      .addCase(editBidderNumberHandle.rejected, (state, action) => {
        state.editBidderNumberError = action.payload;
        state.editBidderNumberloading = false;
      })
      .addCase(updateCompanyProfile.pending, (state) => {
        state.updateCompanyProfileLoading = true;
      })
      .addCase(updateCompanyProfile.fulfilled, (state, action) => {
        state.updateCompanyProfileLoading = false;
      })
      .addCase(updateCompanyProfile.rejected, (state, action) => {
        state.updateCompanyProfileLoading = false;
      })

      .addCase(editTaxExempt.pending, (state) => {
        state.editTaxExemptLoading = true;
      })
      .addCase(editTaxExempt.fulfilled, (state, action) => {
        state.editTaxExemptLoading = false;
      })
      .addCase(editTaxExempt.rejected, (state, action) => {
        state.editTaxExemptLoading = false;
      });
  },
});

export const { clearAddress, clearContactsRes } = companySlice.actions;
export default companySlice.reducer;
